import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { formatDetail } from "@/lib/api";
import { SessionManager } from "@/components/SessionManager";

const AuthContext = createContext(null);
export const EXPIRED_FLAG = "sessionExpired";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null while checking
  const [ready, setReady] = useState(false);
  const [session, setSession] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const clear = () => {
    localStorage.removeItem("apiToken");
    localStorage.removeItem("sessionLastActivity");
    setSession(null);
    setUser(false);
  };

  const expire = useCallback(async () => {
    sessionStorage.setItem(EXPIRED_FLAG, "1");
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    clear();
  }, []);

  useEffect(() => {
    const onExpired = () => { sessionStorage.setItem(EXPIRED_FLAG, "1"); clear(); };
    window.addEventListener("session-expired", onExpired);
    return () => window.removeEventListener("session-expired", onExpired);
  }, []);

  const login = async (phone, password) => {
    const { data } = await api.post("/auth/login", { phone, password });
    localStorage.setItem("apiToken", data.apiToken);
    localStorage.setItem("sessionLastActivity", String(Date.now()));
    sessionStorage.removeItem(EXPIRED_FLAG);
    setSession(data.session);
    setUser(data.user);
    return data.user;
  };

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    clear();
  }, []);

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, formatDetail }}>
      {children}
      {user && <SessionManager key={user.id} session={session} onExpire={expire} onLogout={logout} />}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
